import React, { useState, useEffect } from 'react';
import { emailPlainText } from '../funerals/emailThreads';
import { useEmailBodies } from '../funerals/useEmailBodies';
import { 
  useRecordContext, 
  useUpdate, 
  useNotify,
  useGetList,
  useGetOne,
  useDataProvider,
  useGetIdentity
} from 'react-admin';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select, 
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Alert
} from '@mui/material';
import { 
  NoteAlt as NoteAltIcon,
  LocalShipping as LocalShippingIcon, 
  Schedule as ScheduleIcon, 
  Inventory as InventoryIcon, 
  DeliveryDining as DeliveryDiningIcon, 
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Draw as DrawIcon,
  Payment as PaymentIcon,
  RateReview as RateReviewIcon
} from '@mui/icons-material';
import { 
  CremationStatus,
  Funeral,
  Partner,
  CustomerEvent,
  CustomerEventType,
  TimelineEvent,
  StatusTimelineEvent,
  DeliveryAddress,
  DeliveryMethod,
  ReviewRequest,
  ReviewRequestSendMethod,
  Client
} from '../types';
import { FormControlLabelProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { DeliveryAddressEditor, RecipientType } from './DeliveryAddressEditor';
import { pickupDayTooEarly, transferDayPatch } from '../deliveries/pickupDayRule';
import { recordDayOverride, useRemainsPlan } from '../deliveries/remainsApi';
import { DispatchNotifyPanel, isRecipientNotified } from './dispatch/DispatchNotifyPanel';
import { partnerMatchesRegion } from '../lib/partners';
import { getToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

type PocReadiness =
  | { status: 'exists'; verification_id: string }
  | { status: 'ready' }
  | { status: 'missing_fields'; missing_fields: string[] };

// Status definitions with icons and colors
const statusConfig = {
  [CremationStatus.ARRANGEMENT]: {
    label: 'Arrangement',
    icon: <NoteAltIcon />,
    color: '#e0e0e0'
  },
  [CremationStatus.TRANSPORT_DISPATCHED]: {
    label: 'Transport Dispatched',
    icon: <LocalShippingIcon />,
    color: '#ffecb3'
  },
  [CremationStatus.CREMATION_SCHEDULED]: {
    label: 'Cremation Scheduled',
    icon: <ScheduleIcon />,
    color: '#bbdefb'
  },
  [CremationStatus.READY_FOR_DELIVERY]: {
    label: 'Ready for Delivery',
    icon: <InventoryIcon />,
    color: '#d1c4e9'
  },
  [CremationStatus.DELIVERY_SCHEDULED]: {
    label: 'Delivery Scheduled',
    icon: <DeliveryDiningIcon />,
    color: '#ffcdd2'
  },
  [CremationStatus.DELIVERED]: {
    label: 'Delivered',
    icon: <CheckCircleIcon />,
    color: '#66bb6a'
  }
};

// Days after "Delivered" that the review request goes out.
const REVIEW_REQUEST_DELAY_DAYS = 14;
// Days after delivered_at that the backend auto-sends the aftercare email
// (AftercareService.AUTOSEND_DELAY_DAYS) — display only.
const AFTERCARE_DELAY_DAYS = 5;

// Ordered statuses for progression
const statusOrder = [
  CremationStatus.ARRANGEMENT,
  CremationStatus.TRANSPORT_DISPATCHED,
  CremationStatus.CREMATION_SCHEDULED,
  CremationStatus.READY_FOR_DELIVERY,
  CremationStatus.DELIVERY_SCHEDULED,
  CremationStatus.DELIVERED
];

// Utility function to calculate next business day
const getNextBusinessDay = (date: moment.Moment = moment()): moment.Moment => {
  let nextDay = date.clone().add(1, 'day');
  
  // Keep adding days until we find a weekday (Monday = 1, Friday = 5)
  while (nextDay.day() === 0 || nextDay.day() === 6) { // 0 = Sunday, 6 = Saturday
    nextDay = nextDay.add(1, 'day');
  }
  
  return nextDay;
};

interface CremationTrackerProps {
  /** When true, only render the dialogs (no status chip, button, or timeline) */
  dialogOnly?: boolean;
  /** Controlled open state — when toggled to true, opens the status dialog */
  open?: boolean;
  /** Called when the dialog closes */
  onClose?: () => void;
}

const CremationTracker: React.FC<CremationTrackerProps> = ({ dialogOnly = false, open: externalOpen, onClose: onExternalClose }) => {
  const record = useRecordContext<Funeral>();
  const [update] = useUpdate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<CremationStatus | ''>('');
  const [selectedTransport, setSelectedTransport] = useState<string>('');
  const [selectedCrematorium, setSelectedCrematorium] = useState<string>('');
  // '' = drop the body off at the crematorium (dropoff_partner_id stays null)
  const [selectedDropoff, setSelectedDropoff] = useState<string>('');
  // Set after scheduling a cremation whose Proof of Cremation can't be
  // auto-issued: the dialog stays open showing this warning. [] = the
  // readiness check itself failed.
  const [pocWarning, setPocWarning] = useState<string[] | null>(null);
  const [customerEvents, setCustomerEvents] = useState<CustomerEvent[]>([]);
  
  // New state for additional fields
  const [cremationDate, setCremationDate] = useState<moment.Moment | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<moment.Moment | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress | null>(null);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | ''>('');
  const [pickupLocationId, setPickupLocationId] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [deliveryWindow, setDeliveryWindow] = useState<string>('');
  const [recipientType, setRecipientType] = useState<'primary' | 'authorized' | 'payer' | 'custom' | ''>('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  
  // State for backward navigation confirmation
  // Moving a case to Ready for Delivery sends the family their scheduling link:
  // the status write fires the invite whoever made it, the sweep or a person.
  // Staff assumed the opposite, so the choice is asked for rather than implied.
  const [showNotifyPrompt, setShowNotifyPrompt] = useState(false);
  const [notifyFamilyOnReady, setNotifyFamilyOnReady] = useState(true);
  const [showBackwardConfirmation, setShowBackwardConfirmation] = useState(false);
  const [pendingBackwardStatus, setPendingBackwardStatus] = useState<CremationStatus | null>(null);
  
  // State for review request feature
  const [askForReview, setAskForReview] = useState(false);
  const [reviewRecipientClientId, setReviewRecipientClientId] = useState<number | ''>('');
  const [reviewSendMethod, setReviewSendMethod] = useState<ReviewRequestSendMethod>('email');
  const [reviewMessage, setReviewMessage] = useState('');
  const [clientOptions, setClientOptions] = useState<{id: number; name: string; type: string}[]>([]);

  // Aftercare email opt-out on the Delivered step (backend auto-sends unless disabled)
  const [sendAftercareEmail, setSendAftercareEmail] = useState(true);
  // The earliest office pickup (T-230). The same rule the sidebar and family page use.
  const { plan: remainsPlan } = useRemainsPlan(
    record?.id,
    '',
    selectedStatus === CremationStatus.DELIVERY_SCHEDULED,
  );
  const officePickupRule = remainsPlan?.officePickup ?? null;
  const dayTooEarly = pickupDayTooEarly({
    method: deliveryMethod,
    day: deliveryDate ? deliveryDate.format('YYYY-MM-DD') : null,
    savedDay: record?.delivery_day,
    rule: officePickupRule,
  });
  
  // Fetch transport and crematorium partners for when we need them
  const { data: partnersList, isLoading: isLoadingPartners } = useGetList(
    'partners',
    { 
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' }
    }
  );
  
  // Fetch the deceased client data
  const { data: deceasedClient, isLoading: isLoadingClient } = useGetOne(
    'clients',
    { id: record?.deceased_client_id },
    { enabled: !!record?.deceased_client_id }
  );

  // Next of kin for the ON dispatch email: primary client, else authorized
  const nextOfKinClientId = record?.primary_client_id ?? record?.authorized_client_id;
  const { data: nextOfKinClient } = useGetOne(
    'clients',
    { id: nextOfKinClientId },
    { enabled: !!nextOfKinClientId }
  );
  
  // Filter partners by role capability (a partner can hold several roles) and
  // by market — an ON case only offers ON partners, and vice versa.
  const regionPartners = (partnersList || []).filter(p =>
    partnerMatchesRegion(p as Partner, record?.region),
  );
  const transportPartners = regionPartners.filter(p => p.is_transport);
  const crematoriumPartners = regionPartners.filter(p => p.is_crematorium);
  const dropoffPartners = regionPartners.filter(p => p.is_dropoff_location);

  const timelineEvents = useEmailBodies(customerEvents) ?? customerEvents;

  // Fetch customer events for this funeral
  useEffect(() => {
    if (record?.id) {
      dataProvider.getList('funeral_feed', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'timestamp', order: 'DESC' },
        filter: { funeral_id: record.id },
      }).then(response => {
        setCustomerEvents(response.data);
      }).catch(error => {
        console.error('Error fetching customer events:', error);
      });
    }
  }, [dataProvider, record?.id]);

  // Reflect any existing aftercare preference in the Delivered dialog checkbox
  useEffect(() => {
    if (!record?.id) return;
    dataProvider.getList('aftercare', {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { funeral_id: record.id },
    }).then(response => {
      const row = response.data?.[0];
      if (row) setSendAftercareEmail(row.email_enabled !== false);
    }).catch(() => {
      /* no row yet — the checkbox stays at its default (on) */
    });
  }, [dataProvider, record?.id]);

  // Fetch client options for review request dropdown
  useEffect(() => {
    const fetchClients = async () => {
      if (!record) return;
      
      const clients: {id: number; name: string; type: string}[] = [];
      
      // Fetch primary client
      if (record.primary_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.primary_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Primary'
          });
        } catch (error) {
          console.error('Error fetching primary client:', error);
        }
      }
      
      // Fetch paying client
      if (record.paying_client_id && record.paying_client_id !== record.primary_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.paying_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Paying'
          });
        } catch (error) {
          console.error('Error fetching paying client:', error);
        }
      }
      
      // Fetch authorized client
      if (record.authorized_client_id && 
          record.authorized_client_id !== record.primary_client_id && 
          record.authorized_client_id !== record.paying_client_id) {
        try {
          const response = await dataProvider.getOne('clients', { id: record.authorized_client_id });
          const client = response.data;
          clients.push({
            id: Number(client.id),
            name: client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim(),
            type: 'Authorized'
          });
        } catch (error) {
          console.error('Error fetching authorized client:', error);
        }
      }
      
      setClientOptions(clients);
      
      // Set default recipient to primary client
      if (clients.length > 0) {
        setReviewRecipientClientId(clients[0].id);
      }
    };
    
    void fetchClients();
  }, [dataProvider, record?.primary_client_id, record?.paying_client_id, record?.authorized_client_id]);

  // Set default review message based on region, language, and selected client
  useEffect(() => {
    if (record && reviewRecipientClientId) {
      const reviewLink = record.region === 'ontario'
        ? (process.env.REACT_APP_REVIEW_LINK_ONTARIO || 'https://g.page/r/CaBtI8l6SQNFEBM/review')
        : (process.env.REACT_APP_REVIEW_LINK_QUEBEC || 'https://g.page/r/CWIjv-Au1sTLEBM/review');

      // Get the selected client's first name
      const selectedClient = clientOptions.find(c => c.id === reviewRecipientClientId);
      const firstName = selectedClient?.name?.split(' ')[0] || '';
      const isFrench = record.language === 'fr';

      const defaultMessage = isFrench
        ? `Bonjour${firstName ? ` ${firstName}` : ''}, c'est Bram de Cleo. Je voulais simplement prendre de vos nouvelles et vous remercier encore une fois de nous avoir fait confiance dans un moment aussi difficile.\n\nSi jamais vous vous sentez à l'aise de partager votre expérience, cela aide vraiment d'autres familles à nous trouver : ${reviewLink}\n\nN'hésitez pas à me répondre si vous avez besoin de quoi que ce soit.`
        : `Hi${firstName ? ` ${firstName}` : ''}, it's Bram from Cleo. I'm just checking in to see how you're doing and to thank you again for trusting us during such a difficult time.\n\nIf you ever feel comfortable sharing your experience, it really helps other families find us: ${reviewLink}\n\nYou can always reply here if you need anything.`;
      setReviewMessage(defaultMessage);
    }
  }, [record?.region, record?.language, reviewRecipientClientId, clientOptions]);

  // Function to create a new customer event
  const createCustomerEvent = async (eventData: Partial<CustomerEvent>) => {
    if (!record?.id) return null;
    
    try {
      const event: Partial<CustomerEvent> = {
        funeral_id: Number(record.id),
        timestamp: new Date().toISOString(),
        user_id: identity?.id ? Number(identity.id) : undefined,
        user_name: identity?.fullName || 'System',
        ...eventData
      };
      
      const response = await dataProvider.create('customer_events', { data: event });
      
      // Add the new event to our local state
      setCustomerEvents(prev => [response.data, ...prev]);
      
      return response.data;
    } catch (error) {
      console.error('Error creating customer event:', error);
      return null;
    }
  };

  // No longer manually loading Google Maps API - let the library handle it
  // This fixes ERR_BLOCKED_BY_ORB errors in newer Chrome versions

  // For testing/development - mock data for phone numbers
  useEffect(() => {
    // Only add mock data if in development
    if (process.env.NODE_ENV === 'development') {
      // Add mock phone data to the first transport partner if it exists
      if (transportPartners.length > 0 && !transportPartners[0].phones) {
        transportPartners[0].phones = [
          { label: 'Office', number: '555-123-4567' },
          { label: 'Mobile', number: '555-987-6543' },
          { label: 'After Hours', number: '555-111-2222' }
        ];
      }
    }
  }, [transportPartners]);

  // Seed the transport/crematorium selection from the funeral so the dispatcher sees the
  // crematorium that was locked to the sent agreement (set at contract send). Only fills
  // an empty selection — never overrides an in-progress choice.
  useEffect(() => {
    if (record?.crematorium_partner_id && !selectedCrematorium) {
      setSelectedCrematorium(String(record.crematorium_partner_id));
    }
    if (record?.transport_partner_id && !selectedTransport) {
      setSelectedTransport(String(record.transport_partner_id));
    }
    if (record?.dropoff_partner_id && !selectedDropoff) {
      setSelectedDropoff(String(record.dropoff_partner_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.crematorium_partner_id, record?.transport_partner_id, record?.dropoff_partner_id]);

  // -- External dialog control --
  const handleOpenDialogRef = React.useRef<(() => void) | null>(null);
  const prevExternalOpen = React.useRef(false);
  const wasDialogOpenRef = React.useRef(false);

  // Open dialog when external open signal transitions to true
  useEffect(() => {
    if (externalOpen && !prevExternalOpen.current) {
      handleOpenDialogRef.current?.();
    }
    prevExternalOpen.current = !!externalOpen;
  }, [externalOpen]);

  // Notify parent when dialog closes
  useEffect(() => {
    if (wasDialogOpenRef.current && !dialogOpen && onExternalClose) {
      onExternalClose();
    }
    wasDialogOpenRef.current = dialogOpen;
  }, [dialogOpen, onExternalClose]);

  if (!record) return null;

  // Show loading state if client data is being fetched
  if (isLoadingClient) {
    return (
      <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Default to ARRANGEMENT if no status is set
  const currentStatus = record.cremation_status || CremationStatus.ARRANGEMENT;
  const currentStatusIndex = statusOrder.indexOf(currentStatus as CremationStatus);
  
  // Custom timeline rendering without using @mui/lab
  const renderTimelineEvents = () => {
    // Combine status change events and customer events into a single timeline
    // We use StatusTimelineEvent to ensure all events have a status
    const allTimelineEvents: StatusTimelineEvent[] = [];
    
    // Add status change events from record
    if (record.status_history && Array.isArray(record.status_history)) {
      // Type safety: ensure all events have a status property
      record.status_history.forEach(event => {
        if (event.status && event.user && event.user !== 'System') {
          allTimelineEvents.push({
            ...event,
            status: event.status // Ensure status is present
          } as StatusTimelineEvent);
        }
      });
    } 
    // We no longer create mock history for system events

    // Add customer events
    timelineEvents.forEach(event => {
      if (event.status) {
        // Only add events with a status to maintain type safety
        allTimelineEvents.push({
          status: event.status,
          timestamp: event.timestamp,
          user: event.user_name,
          type: event.type,
          recipient_name: event.recipient_name,
          contact_method: event.contact_method,
          contact_detail: event.contact_detail,
          subject: event.subject,
          message: event.message
        });
      } else if (event.type === CustomerEventType.STATUS_CHANGE) {
        // For status change events without a status (shouldn't happen but just in case)
        allTimelineEvents.push({
          status: CremationStatus.ARRANGEMENT, // Default fallback
          timestamp: event.timestamp,
          user: event.user_name,
          type: event.type,
          message: event.message
        });
      } else {
        // For non-status events like calls, emails, texts - create with a dummy status
        allTimelineEvents.push({
          status: CremationStatus.ARRANGEMENT, // Default fallback
          timestamp: event.timestamp,
          user: event.user_name,
          type: event.type,
          recipient_name: event.recipient_name,
          contact_method: event.contact_method,
          contact_detail: event.contact_detail,
          subject: event.subject,
          message: event.type === CustomerEventType.EMAIL ? emailPlainText(event) : event.message
        });
      }
    });

    // Sort timeline events by timestamp (newest first)
    allTimelineEvents.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    // Filter out system status events
    const filteredEvents = allTimelineEvents.filter(event => 
      // Keep all customer event types
      event.type ||
      // For regular status events, only keep those that have a real user and aren't system-generated
      (event.user && event.user !== 'System')
    );

    return (
      <Box sx={{ paddingLeft: 2 }}>
        {filteredEvents.map((event, index) => {
          // Configure the display based on event type
          let icon = <NoteAltIcon />;
          let color = '#757575';
          let label = 'Event';
          
          // Status change events
          if (event.status && !event.type) {
            // Use safe indexed access with default fallback
            const statusKey = event.status as CremationStatus;
            const config = statusConfig[statusKey] || {
              label: 'Unknown',
              icon: <NoteAltIcon />,
              color: '#757575'
            };
            icon = config.icon;
            color = config.color;
            label = config.label;
          } 
          // Customer events
          else if (event.type) {
            switch(event.type) {
              case CustomerEventType.PHONE_CALL:
                icon = <PhoneIcon />;
                color = '#4caf50'; // Green
                label = 'Phone Call';
                break;
              case CustomerEventType.TEXT_MESSAGE:
                icon = <MessageIcon />;
                color = '#2196f3'; // Blue
                label = 'Text Message';
                break;
              case CustomerEventType.EMAIL:
                icon = <EmailIcon />;
                color = '#9c27b0'; // Purple
                label = 'Email';
                break;
              case CustomerEventType.CPP_BENEFITS_MAILED:
                icon = <EmailIcon />;
                color = '#2196f3'; // Blue
                label = 'CPP Benefits Mailed';
                break;
              case CustomerEventType.MAIL_SENT:
                icon = <EmailIcon />;
                color = '#795548'; // Brown
                label = 'Physical Mail Sent';
                break;
              case CustomerEventType.STATUS_CHANGE:
                if (event.status) {
                  const statusKey = event.status as CremationStatus;
                  if (statusConfig[statusKey]) {
                    icon = statusConfig[statusKey].icon;
                    color = statusConfig[statusKey].color;
                    label = `Status: ${statusConfig[statusKey].label}`;
                  } else {
                    icon = <NoteAltIcon />;
                    color = '#757575';
                    label = 'Status Change';
                  }
                } else {
                  icon = <NoteAltIcon />;
                  color = '#757575';
                  label = 'Status Change';
                }
                break;
              case CustomerEventType.CUSTOMER_MESSAGE:
                icon = <MessageIcon />;
                color = '#ff9800'; // Orange
                label = 'Customer Message';
                break;
              case CustomerEventType.CONTRACT_SENT:
                icon = <DescriptionIcon />;
                color = '#3f51b5'; // Indigo
                label = 'Contract Sent';
                break;
              case CustomerEventType.CONTRACT_OPENED:
                icon = <VisibilityIcon />;
                color = '#00bcd4'; // Cyan
                label = 'Contract Opened';
                break;
              case CustomerEventType.CONTRACT_SIGNED:
                icon = <DrawIcon />;
                color = '#4caf50'; // Green
                label = 'Contract Signed';
                break;
              case CustomerEventType.CONTRACT_PAID:
                icon = <PaymentIcon />;
                color = '#66bb6a'; // Light Green
                label = 'Contract Fulfilled';
                break;
              case CustomerEventType.REVIEW_REQUEST_SENT:
                icon = <RateReviewIcon />;
                color = '#ffc107'; // Amber
                label = 'Review Request Sent';
                break;
              default:
                icon = <NoteAltIcon />;
                color = '#757575';
                label = 'Event';
            }
          }
          
          const date = new Date(event.timestamp);
          const formattedDate = date.toLocaleDateString();
          const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          
          const isLast = index === filteredEvents.length - 1;
          
          return (
            <Box key={index} sx={{ display: 'flex', marginBottom: isLast ? 0 : 2 }}>
              {/* Time column */}
              <Box sx={{ width: '100px', paddingRight: 2, textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  {formattedDate}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formattedTime}
                </Typography>
              </Box>
              
              {/* Icon and line */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingX: 1 }}>
                <Avatar sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: color,
                  color: event.status && statusConfig[event.status as CremationStatus] ? 'rgba(0, 0, 0, 0.87)' : 'white'
                }}>
                  {React.cloneElement(icon, { fontSize: 'small' })}
                </Avatar>
                {!isLast && (
                  <Box sx={{ 
                    width: 2, 
                    bgcolor: '#e0e0e0', 
                    flexGrow: 1, 
                    marginY: 0.5 
                  }} />
                )}
              </Box>
              
              {/* Content */}
              <Box sx={{ 
                flex: 1, 
                paddingLeft: 2,
                paddingY: 1,
                paddingRight: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                marginBottom: 1
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">{label}</Typography>
                  <Chip 
                    size="small"
                    label={event.user || 'System'} 
                    sx={{ bgcolor: 'white' }}
                  />
                </Box>
                
                {/* Status change content */}
                {event.status && !event.type && (
                  <>
                    {event.notes && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {event.notes}
                      </Typography>
                    )}
                    
                    {/* Show cremation date for cremation scheduled events */}
                    {event.status === CremationStatus.CREMATION_SCHEDULED && record?.cremation_date && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        <strong>Cremation Date:</strong> {moment(record.cremation_date).format('MMMM D, YYYY')}
                      </Typography>
                    )}
                    
                    {/* Show delivery details for delivery scheduled events */}
                    {event.status === CremationStatus.DELIVERY_SCHEDULED && (
                      <>
                        {record?.delivery_day && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            <strong>Delivery Date:</strong> {moment(record.delivery_day).format('MMMM D, YYYY')}
                          </Typography>
                        )}
                        
                        {record?.delivery_method && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            <strong>Delivery Method:</strong> {
                              record.delivery_method === 'shipping' ? 'Shipping (Canada Post)' :
                              record.delivery_method === 'courier' ? 'Courier (Courrier Plus)' :
                              record.delivery_method === 'office_pickup' ? 'Pickup' :
                              record.delivery_method === 'crematorium_pickup' ? 'Pickup' :
                              record.delivery_method === 'hand_delivery' ? 'Hand Delivery' :
                              record.delivery_method === 'pickup' ? 'Customer Pickup' :
                              record.delivery_method
                            }
                          </Typography>
                        )}
                        
                        {record?.delivery_window && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            <strong>Delivery Window:</strong> {record.delivery_window}
                          </Typography>
                        )}
                        
                        {record?.delivery_address && (() => {
                          // Helper to format delivery address, handling null values
                          const formatAddress = (addr: DeliveryAddress) => {
                            const parts = [];
                            
                            // Format street address (apartment-civic_number address format)
                            const streetParts = [];
                            if (addr.apartment && addr.civic_number) {
                              streetParts.push(`${addr.apartment}-${addr.civic_number}`);
                            } else if (addr.apartment) {
                              streetParts.push(addr.apartment);
                            } else if (addr.civic_number) {
                              streetParts.push(addr.civic_number);
                            }
                            if (addr.address) {
                              streetParts.push(addr.address);
                            }
                            if (streetParts.length > 0) {
                              parts.push(streetParts.join(' '));
                            }
                            
                            if (addr.city) parts.push(addr.city);
                            if (addr.province) parts.push(addr.province);
                            if (addr.postal_code) parts.push(addr.postal_code);
                            
                            return parts.length > 0 ? parts.join(', ') : addr.formatted_address || 'Not set';
                          };
                          
                          return (
                            <Typography variant="body2" color="text.secondary" mt={1}>
                              <strong>Delivery Address:</strong> {formatAddress(record.delivery_address)}
                            </Typography>
                          );
                        })()}
                        
                        {record?.delivery_address?.recipient_name && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            <strong>Recipient:</strong> {record.delivery_address.recipient_name}
                            {record.delivery_address.recipient_phone && ` (${record.delivery_address.recipient_phone})`}
                          </Typography>
                        )}
                        
                        {record?.delivery_note && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            mt={1}
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              bgcolor: 'rgba(255, 255, 255, 0.5)',
                              p: 1,
                              borderRadius: 1
                            }}
                          >
                            <strong>Delivery Note:</strong> {record.delivery_note}
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Customer event content */}
                {event.type && (
                  <>
                    {/* Show status change details for customer events that are status changes */}
                    {event.type === CustomerEventType.STATUS_CHANGE && event.message && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        mt={1}
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          bgcolor: 'rgba(255, 255, 255, 0.5)',
                          p: 1,
                          borderRadius: 1
                        }}
                      >
                        {event.message}
                      </Typography>
                    )}
                    
                    {/* Regular customer event content (calls, emails, texts) */}
                    {event.type !== CustomerEventType.STATUS_CHANGE && (
                      <>
                        {event.recipient_name && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            <strong>To:</strong> {event.recipient_name}
                            {event.contact_detail && ` (${event.contact_detail})`}
                          </Typography>
                        )}
                        
                        {event.subject && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            <strong>Subject:</strong> {event.subject}
                          </Typography>
                        )}
                        
                        {event.message && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            mt={0.5}
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              maxHeight: '80px',
                              overflow: 'auto',
                              bgcolor: 'rgba(255, 255, 255, 0.5)',
                              p: 1,
                              borderRadius: 1
                            }}
                          >
                            {event.message}
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {event.communicationSent && (
                  <Typography variant="body2" color="primary" mt={1}>
                    Communication sent to customer
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Two separate questions, and one flag was answering both: `skipConfirmation`
  // means "the backward-move dialog has been answered", `skipNotifyPrompt` means
  // "the notify question has been answered". Conflating them meant a backward
  // move into Ready for Delivery re-entered with both suppressed, so the notify
  // prompt never appeared and the default (notify) silently cleared a
  // suppression staff had deliberately set.
  const handleStatusChange = async (
    newStatus: CremationStatus,
    skipConfirmation = false,
    skipNotifyPrompt = false,
  ) => {
    // Skip if new status is the same as current status (which may be the default ARRANGEMENT)
    if (!newStatus || newStatus === currentStatus) return;
    
    // Check if this is a backward navigation
    const currentIndex = statusOrder.indexOf(currentStatus as CremationStatus);
    const newIndex = statusOrder.indexOf(newStatus);
    const isBackward = newIndex < currentIndex;
    
    // Show confirmation dialog for backward navigation
    if (isBackward && !skipConfirmation) {
      setPendingBackwardStatus(newStatus);
      setShowBackwardConfirmation(true);
      return;
    }

    // Ready for Delivery is the one status that messages the family on its own.
    // Ask before it happens — afterwards the text has gone.
    if (newStatus === CremationStatus.READY_FOR_DELIVERY && !skipNotifyPrompt) {
      setNotifyFamilyOnReady(true);
      setShowNotifyPrompt(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare the update data with the new status
      const updateData: any = { 
        cremation_status: newStatus,
      };
      
      // Add additional fields based on the status
      if (newStatus === CremationStatus.TRANSPORT_DISPATCHED) {
        updateData.transport_dispatched_date = new Date().toISOString();
      }

      if (newStatus === CremationStatus.DELIVERED) {
        updateData.delivered_at = new Date().toISOString();
      }

      // Written with the status, because the invite job reads the case after the
      // status lands and has nobody to ask. Ticking it again clears the
      // suppression, so a case held back once can be sent later.
      if (newStatus === CremationStatus.READY_FOR_DELIVERY) {
        updateData.delivery_invite_suppressed_at = notifyFamilyOnReady
          ? null
          : new Date().toISOString();
      }
      
      if (newStatus === CremationStatus.CREMATION_SCHEDULED && cremationDate) {
        updateData.cremation_date = cremationDate.toISOString();
      }
      
      if (newStatus === CremationStatus.DELIVERY_SCHEDULED) {
        if (deliveryDate) {
          updateData.delivery_day = deliveryDate.format('YYYY-MM-DD');
        }
        if (deliveryNote) {
          updateData.delivery_note = deliveryNote;
        }
        if (deliveryMethod) {
          updateData.delivery_method = deliveryMethod;
        }
        if (deliveryWindow) {
          updateData.delivery_window = deliveryWindow;
        }
        // Build delivery address object with recipient info
        const addressData: DeliveryAddress = {
          ...deliveryAddress,
          selection: recipientType || deliveryAddress?.selection,
          recipient_name: recipientName || deliveryAddress?.recipient_name,
          recipient_phone: recipientPhone || deliveryAddress?.recipient_phone,
        };
        updateData.delivery_address = addressData;
        // Staff setting the details by hand counts as confirmed. Stamping it here
        // is what stops the family invite and the agent from chasing a case that
        // is already arranged.
        updateData.delivery_confirmed_at =
          record.delivery_confirmed_at || new Date().toISOString();
        updateData.delivery_confirmed_by = record.delivery_confirmed_by || 'staff';
        updateData.pickup_location_id =
          deliveryMethod === 'office_pickup' || deliveryMethod === 'crematorium_pickup'
            ? pickupLocationId
            : null;
        Object.assign(
          updateData,
          transferDayPatch({
            method: deliveryMethod,
            day: deliveryDate ? deliveryDate.format('YYYY-MM-DD') : record.delivery_day,
            savedMethod: record.delivery_method,
            savedDay: record.delivery_day,
            savedTransferDay: record.transfer_day,
            courierBooked: !!record.courier_call_number,
            rule: officePickupRule,
          }),
        );
      }
      
      // Update the status in the database
      await update(
        'funerals',
        {
          id: record.id,
          data: updateData,
          previousData: record
        },
        {
          onSuccess: async () => {
            if (newStatus === CremationStatus.DELIVERY_SCHEDULED && dayTooEarly && officePickupRule && deliveryDate) {
              void recordDayOverride(record.id, {
                day: deliveryDate.format('YYYY-MM-DD'),
                earliest: officePickupRule.earliestDay,
                reason: overrideReason,
              }).catch(() =>
                notify('The reason for the early date was not recorded', { type: 'warning' }),
              );
            }
            // Create a customer event for the status change with additional data
            const eventMessage = `Status updated to ${statusConfig[newStatus].label}`;
            let eventDetails = eventMessage;
            
            // Add specific details based on status
            if (newStatus === CremationStatus.CREMATION_SCHEDULED && cremationDate) {
              eventDetails += `\nCremation Date: ${cremationDate.format('MMMM D, YYYY')}`;
            }
            
            if (newStatus === CremationStatus.DELIVERY_SCHEDULED) {
              if (deliveryDate) {
                eventDetails += `\nDelivery Date: ${deliveryDate.format('MMMM D, YYYY')}`;
              }
              if (deliveryMethod) {
                const methodLabels: Record<string, string> = {
                  shipping: 'Shipping (Canada Post)',
                  courier: 'Courier (Courrier Plus)',
                  office_pickup: 'Pickup',
                  crematorium_pickup: 'Pickup',
                  // Legacy, still on older cases.
                  hand_delivery: 'Hand Delivery',
                  pickup: 'Customer Pickup'
                };
                eventDetails += `\nDelivery Method: ${methodLabels[deliveryMethod] || deliveryMethod}`;
              }
              if (deliveryWindow) {
                eventDetails += `\nDelivery Window: ${deliveryWindow}`;
              }
              if (deliveryAddress?.formatted_address || deliveryAddress?.address) {
                eventDetails += `\nDelivery Address: ${deliveryAddress.formatted_address || deliveryAddress.address}`;
              }
              if (recipientName) {
                eventDetails += `\nRecipient: ${recipientName}`;
              }
              if (recipientPhone) {
                eventDetails += `\nRecipient Phone: ${recipientPhone}`;
              }
              if (deliveryNote) {
                eventDetails += `\nDelivery Note: ${deliveryNote}`;
              }
            }
            
            await createCustomerEvent({
              type: CustomerEventType.STATUS_CHANGE,
              status: newStatus,
              message: eventDetails,
            });

            // Canada Post with no day: let the server set the next pickup at
            // this crematorium now rather than at its next half-hourly sweep
            // (T-103). It never overwrites a day, so a no-op when one was set.
            if (
              newStatus === CremationStatus.DELIVERY_SCHEDULED &&
              deliveryMethod === 'shipping' &&
              !deliveryDate
            ) {
              void getToken()
                .then((token) =>
                  fetch(`${PAPERMAN_API_URL}delivery-scheduling/staff/${record.id}/ship-day`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  })
                )
                .catch(() => undefined);
            }
            
            // Create review request if opted in and status is DELIVERED
            if (newStatus === CremationStatus.DELIVERED && askForReview && reviewRecipientClientId) {
              try {
                const scheduledDate = moment().add(REVIEW_REQUEST_DELAY_DAYS, 'days').toISOString();

                await dataProvider.create('review_requests', {
                  data: {
                    funeral_id: Number(record.id),
                    enabled: true,
                    send_method: reviewSendMethod,
                    recipient_client_id: reviewRecipientClientId,
                    message: reviewMessage,
                    scheduled_date: scheduledDate,
                    created_by_user_id: identity?.id ? Number(identity.id) : null,
                    created_by_user_name: identity?.fullName || 'System'
                  }
                });

                notify(`Review request scheduled for ${REVIEW_REQUEST_DELAY_DAYS} days from now`, { type: 'info' });
              } catch (reviewError) {
                console.error('Error creating review request:', reviewError);
                notify('Status updated, but failed to schedule review request', { type: 'warning' });
              }
            }

            // Persist the aftercare email choice. The backend auto-sends
            // AFTERCARE_DELAY_DAYS after delivered_at unless the aftercare row
            // is disabled, so all this has to do is record the toggle.
            if (newStatus === CremationStatus.DELIVERED) {
              try {
                const existing = await dataProvider.getList('aftercare', {
                  pagination: { page: 1, perPage: 1 },
                  sort: { field: 'id', order: 'DESC' },
                  filter: { funeral_id: record.id },
                });
                const aftercareRow = existing.data?.[0];

                if (aftercareRow) {
                  if (aftercareRow.email_enabled !== sendAftercareEmail) {
                    await dataProvider.update('aftercare', {
                      id: aftercareRow.id,
                      data: { email_enabled: sendAftercareEmail },
                      previousData: aftercareRow,
                    });
                  }
                } else {
                  await dataProvider.create('aftercare', {
                    data: {
                      funeral_id: Number(record.id),
                      email_enabled: sendAftercareEmail,
                      email_recipient_client_id: record.primary_client_id ?? null,
                      created_by_user_id: identity?.id ? Number(identity.id) : null,
                      created_by_user_name: identity?.fullName || 'System',
                    },
                  });
                }

                notify(
                  sendAftercareEmail
                    ? `Aftercare email will be sent ${AFTERCARE_DELAY_DAYS} days after delivery`
                    : 'Aftercare email turned off for this case',
                  { type: 'info' },
                );
              } catch (aftercareError) {
                console.error('Error saving aftercare preference:', aftercareError);
                notify('Status updated, but failed to save the aftercare email setting', { type: 'warning' });
              }
            }

            notify('Status updated successfully', { type: 'success' });

            // The POC itself is issued server-side by the cremation_status
            // trigger; this only tells staff when it won't be, so they create it
            // by hand. Never blocks the status change.
            if (newStatus === CremationStatus.CREMATION_SCHEDULED) {
              const poc = await fetchPocReadiness(record.id);
              if (!poc || poc.status === 'missing_fields') {
                setPocWarning(poc?.missing_fields ?? []);
                return;
              }
            }
            setDialogOpen(false);
          },
          onError: (error: unknown) => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify(`Error: ${errorMessage}`, { type: 'error' });
          }
        }
      );
    } catch (error) {
      notify(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPocReadiness = async (funeralId: Funeral['id']): Promise<PocReadiness | null> => {
    try {
      const token = await getToken();
      const response = await fetch(`${PAPERMAN_API_URL}documents/funeral/${funeralId}/poc-readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return (await response.json()) as PocReadiness;
    } catch (error) {
      console.error('POC readiness check failed:', error);
      return null;
    }
  };

  const handleConfirmBackwardNavigation = async () => {
    if (pendingBackwardStatus) {
      setShowBackwardConfirmation(false);
      await handleStatusChange(pendingBackwardStatus, true); // Skip confirmation on retry
      setPendingBackwardStatus(null);
    }
  };

  const handleCancelBackwardNavigation = () => {
    setShowBackwardConfirmation(false);
    setPendingBackwardStatus(null);
  };

  const handleOpenDialog = () => {
    setOverrideReason('');
    // Set default to next status in the process
    const currentIndex = statusOrder.indexOf(currentStatus as CremationStatus);
    if (currentIndex >= 0 && currentIndex < statusOrder.length - 1) {
      // If we have a valid current status, set to next status
      const nextStatus = statusOrder[currentIndex + 1];
      setSelectedStatus(nextStatus);
      
      // Set default dates based on the next status
      if (nextStatus === CremationStatus.CREMATION_SCHEDULED) {
        setCremationDate(moment()); // Default to today
      } else if (nextStatus === CremationStatus.DELIVERY_SCHEDULED) {
        // Initialize with existing values or defaults
        setDeliveryDate(record.delivery_day ? moment(record.delivery_day) : getNextBusinessDay());
        setDeliveryNote(record.delivery_note || '');
        setDeliveryMethod(record.delivery_method || '');
        setPickupLocationId(record.pickup_location_id || null);
        setDeliveryWindow(record.delivery_window || '');
        
        // Check if delivery address already exists
        if (record.delivery_address && record.delivery_address.selection) {
          // Use existing delivery address data
          const existingAddress = record.delivery_address;
          
          // Build formatted_address if it doesn't exist
          let formattedAddress = existingAddress.formatted_address;
          if (!formattedAddress && existingAddress.address) {
            const parts = [
              existingAddress.address,
              existingAddress.apartment ? `Apt ${existingAddress.apartment}` : '',
              existingAddress.city,
              existingAddress.province,
              existingAddress.postal_code
            ].filter(Boolean);
            formattedAddress = parts.join(', ');
          }
          
          setDeliveryAddress({
            ...existingAddress,
            formatted_address: formattedAddress
          });
          setRecipientType(existingAddress.selection as any);
          setRecipientName(existingAddress.recipient_name || '');
          setRecipientPhone(existingAddress.recipient_phone || '');
        } else {
          // No existing delivery address, default to primary client
          setDeliveryAddress(null);
          setRecipientType('primary');
          
          // Fetch primary client data
          if (record.primary_client_id) {
            dataProvider.getOne('clients', { id: record.primary_client_id })
              .then(response => {
                const client = response.data;
                setRecipientName(`${client.first_name || ''} ${client.last_name || ''}`.trim());
                setRecipientPhone(client.phone_number || '');
                // Set address from client
                const streetParts = [];
                if (client.civic_number) streetParts.push(client.civic_number);
                if (client.address) streetParts.push(client.address);
                const streetDisplay = streetParts.join(' ');
                setDeliveryAddress({
                  address: client.address,
                  civic_number: client.civic_number,
                  city: client.city,
                  province: client.province,
                  postal_code: client.postal_code,
                  formatted_address: `${streetDisplay}, ${client.city}, ${client.province} ${client.postal_code}`,
                  selection: 'primary',
                });
              })
              .catch(error => console.error('Error fetching client:', error));
          } else {
            // No primary client, clear fields
            setRecipientName('');
            setRecipientPhone('');
          }
        }
      }
    } else if (currentIndex === -1) {
      // If current status is not in the list, default to ARRANGEMENT
      setSelectedStatus(CremationStatus.ARRANGEMENT);
    } else {
      // If at the end of the list, don't preselect anything
      setSelectedStatus('');
    }
    setPocWarning(null);
    setDialogOpen(true);
  };

  // Keep ref in sync for external triggering
  handleOpenDialogRef.current = handleOpenDialog;

  // Get available statuses based on current status (allow backward and forward navigation)
  const getAvailableStatuses = () => {
    const currentIndex = statusOrder.indexOf(currentStatus as CremationStatus);
    if (currentIndex === -1) return statusOrder; // If no current status, show all
    
    // Allow navigation to any previous status or the next status
    // Don't allow skipping ahead more than one step forward
    const availableStatuses: CremationStatus[] = [];
    
    // Add all previous statuses (allow going backward)
    for (let i = 0; i <= currentIndex; i++) {
      availableStatuses.push(statusOrder[i]);
    }
    
    // Add next status if not at the end (allow going one step forward)
    if (currentIndex < statusOrder.length - 1) {
      availableStatuses.push(statusOrder[currentIndex + 1]);
    }
    
    return availableStatuses;
  };

  const handleTransportDispatch = async () => {
    if (!selectedTransport || !selectedCrematorium) {
      notify('Please select both a transport partner and crematorium', { type: 'warning' });
      return;
    }

    // Warn when a required recipient has no logged contact. Derived from
    // customer_events so prior contact (e.g. a teammate already called)
    // suppresses the warning. Required set is region-aware: QC = transport +
    // crematorium; ON = transport + wherever the body is being dropped off
    // (the crematorium notification is optional once a distinct drop-off
    // location is selected).
    const isOntario = record?.region === 'ontario';
    const distinctDropoff =
      isOntario && !!selectedDropoff && selectedDropoff !== selectedCrematorium;

    const missing: string[] = [];
    if (!isRecipientNotified(customerEvents, 'transport', selectedTransport)) {
      missing.push('the transport company');
    }
    if (isOntario) {
      const destinationNotified = distinctDropoff
        ? isRecipientNotified(customerEvents, 'dropoff', selectedDropoff)
        : isRecipientNotified(customerEvents, 'crematorium', selectedCrematorium);
      if (!destinationNotified) {
        missing.push(
          distinctDropoff ? 'the drop-off location' : 'the crematorium (drop-off location)',
        );
      }
    } else if (!isRecipientNotified(customerEvents, 'crematorium', selectedCrematorium)) {
      missing.push('the crematorium');
    }

    if (missing.length > 0) {
      const target = missing.join(' and ');
      const verb = missing.length > 1 ? 'them' : 'it';
      const confirmMsg = `No call, text, or email to ${target} is logged for this funeral. Dispatch transport without notifying ${verb}?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    // Warn if dispatching to a crematorium that differs from the one locked to the sent
    // agreement — the signed cremation agreement is crematorium-specific.
    const lockedCrematorium = record?.crematorium_partner_id?.toString() ?? '';
    if (
      lockedCrematorium &&
      record?.contracts_sent_at &&
      String(selectedCrematorium) !== lockedCrematorium
    ) {
      const lockedName =
        crematoriumPartners.find(p => p.id?.toString() === lockedCrematorium)?.name ??
        'the crematorium on the signed agreement';
      const proceed = window.confirm(
        `The cremation agreement was sent for ${lockedName}, but you are dispatching to a different ` +
          `crematorium. This may not match the agreement the family signed. Dispatch anyway?`,
      );
      if (!proceed) return;
    }

    try {
      setLoading(true);
      
      const transportPartner = transportPartners.find(p => p.id === selectedTransport);
      const crematoriumPartner = crematoriumPartners.find(p => p.id === selectedCrematorium);
      
      // Debug logging
      console.log('Selected transport partner:', transportPartner);
      console.log('Selected crematorium partner:', crematoriumPartner);
      console.log('Selected transport ID:', selectedTransport, typeof selectedTransport);
      console.log('Selected crematorium ID:', selectedCrematorium, typeof selectedCrematorium);
      
      if (!transportPartner || !crematoriumPartner) {
        notify('Partner information not found', { type: 'error' });
        return;
      }
      
      // dropoff_partner_id null = body is dropped off at the crematorium.
      // Never store the crematorium's own id here — a later crematorium
      // change would silently desync the two.
      const dropoffPartner = distinctDropoff
        ? dropoffPartners.find(p => p.id === selectedDropoff)
        : null;

      const updateData = {
        cremation_status: CremationStatus.TRANSPORT_DISPATCHED,
        // Dispatch has its own dialog and never goes through the generic
        // status-change path above, which is the only other place that sets
        // this. Omitting it here left the column null on all 640 cases since it
        // was added: the drop-off portal's storage clock and the alternate-
        // crematorium monthly tally both read it and both saw nothing.
        transport_dispatched_date: new Date().toISOString(),
        transport_partner_id: selectedTransport,
        crematorium_partner_id: selectedCrematorium,
        dropoff_partner_id: distinctDropoff ? selectedDropoff : null
      };
      
      console.log('Updating funeral with data:', updateData);
      
      await update(
        'funerals',
        {
          id: record.id,
          data: updateData,
          previousData: record
        },
        {
          onSuccess: async (response) => {
            console.log('Update success response:', response);
            // Create a customer event for the transport dispatch
            await createCustomerEvent({
              type: CustomerEventType.STATUS_CHANGE,
              status: CremationStatus.TRANSPORT_DISPATCHED,
              message: `Status updated to Transport Dispatched\nTransport: ${transportPartner.name}\nCrematorium: ${crematoriumPartner.name}${dropoffPartner ? `\nDrop-off: ${dropoffPartner.name}` : ''}`
            });
            
            notify('Transport dispatched successfully', { type: 'success' });
            setDialogOpen(false);
          },
          onError: (error: unknown) => {
            console.error('Update error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify(`Error: ${errorMessage}`, { type: 'error' });
          }
        }
      );
    } catch (error) {
      console.error('Transport dispatch error:', error);
      notify(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // The drop-off facility normally records the handoff itself from the partner
  // portal. This is the staff-side backstop for when they forget to click, or
  // click the wrong case — same write, same event, either direction.
  const handleToggleDropoffRelease = async () => {
    if (!record?.id) return;
    const releasing = !record.dropoff_released_at;
    setLoading(true);
    try {
      await update(
        'funerals',
        {
          id: record.id,
          data: releasing
            ? {
                dropoff_released_at: new Date().toISOString(),
                dropoff_released_by: identity?.fullName || 'Cleo staff',
              }
            : { dropoff_released_at: null, dropoff_released_by: null },
          previousData: record,
        },
        { returnPromise: true }
      );

      const dropoffName =
        dropoffPartners.find(p => p.id === String(record.dropoff_partner_id))?.name ||
        'the drop-off location';
      // An internal note, not a status_change: cremation_status genuinely has
      // not moved, and status_change rows trigger a case-metrics recompute that
      // has nothing to learn from this.
      await createCustomerEvent({
        type: CustomerEventType.INTERNAL_NOTE,
        message: releasing
          ? `Released to crematorium from ${dropoffName}`
          : `Release to crematorium undone for ${dropoffName}`,
      });

      notify(releasing ? 'Released to crematorium' : 'Release undone', { type: 'success' });
    } catch (error) {
      console.error('Error toggling drop-off release:', error);
      notify('Failed to update the drop-off release', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Update the Dialog content to include transport and crematorium controls
  const renderDialogContent = () => {
    // Skip if not selecting one of the available statuses
    if (!selectedStatus) return null;

    // For Transport Dispatched status, show the partner selection
    if (selectedStatus === CremationStatus.TRANSPORT_DISPATCHED) {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {record?.region === 'ontario'
              ? 'Select and notify the transport company and drop-off location, then dispatch transport.'
              : 'Select and notify the transport company and crematorium, then dispatch transport.'}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <DispatchNotifyPanel
              record={record}
              deceasedClient={deceasedClient}
              nextOfKinClient={nextOfKinClient}
              customerEvents={customerEvents}
              createCustomerEvent={createCustomerEvent}
              transportPartners={transportPartners}
              crematoriumPartners={crematoriumPartners}
              dropoffPartners={dropoffPartners}
              isLoadingPartners={isLoadingPartners}
              selectedTransport={selectedTransport}
              onTransportChange={setSelectedTransport}
              selectedCrematorium={selectedCrematorium}
              onCrematoriumChange={setSelectedCrematorium}
              selectedDropoff={selectedDropoff}
              onDropoffChange={setSelectedDropoff}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleTransportDispatch}
            disabled={!selectedTransport || !selectedCrematorium || loading}
            fullWidth
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: '#5e35b1',
              '&:hover': {
                backgroundColor: '#4527a0',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <Typography sx={{
                fontWeight: 500,
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                DISPATCH TRANSPORT
              </Typography>
            )}
          </Button>
        </>
      );
    }
    
    // Always show status dropdown first (except for Transport Dispatched which has its own UI)
    // Default status update content
        return (
          <>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="status-select-label">New Status</InputLabel>
          <Select
            labelId="status-select-label"
            value={selectedStatus}
            label="New Status"
            onChange={(e) => setSelectedStatus(e.target.value as CremationStatus)}
          >
            <MenuItem value="" disabled>
              <em>Select a status</em>
            </MenuItem>
            {getAvailableStatuses().map((status) => {
              const currentIndex = statusOrder.indexOf(currentStatus as CremationStatus);
              const statusIndex = statusOrder.indexOf(status);
              const isBackward = statusIndex < currentIndex;
              const isCurrent = status === currentStatus;
              const isForward = statusIndex > currentIndex;
              
              return (
                <MenuItem key={status} value={status} disabled={isCurrent}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ mr: 1 }}>
                      {isBackward && '↩️'}
                      {isCurrent && '📍'}
                      {isForward && '➡️'}
                    </Box>
                    <Typography sx={{ 
                      color: isBackward ? '#ff9800' : isCurrent ? '#757575' : 'inherit',
                      fontWeight: isCurrent ? 'bold' : 'normal'
                    }}>
                      {statusConfig[status].label}
                      {isBackward && ' (Go Back)'}
                      {isCurrent && ' (Current)'}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        
        {/* Conditional fields based on selected status */}
        {selectedStatus === CremationStatus.CREMATION_SCHEDULED && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mt: 2, mb: 2 }}>
              <DatePicker
                label="Cremation Date"
                value={cremationDate}
                onChange={(newValue) => setCremationDate(newValue as moment.Moment | null)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>
        )}
        
        {selectedStatus === CremationStatus.DELIVERY_SCHEDULED && (
          <Box sx={{ mt: 2 }}>
            <DeliveryAddressEditor
              deliveryAddress={deliveryAddress}
              deliveryDate={deliveryDate}
              deliveryWindow={deliveryWindow}
              deliveryMethod={deliveryMethod}
              pickupLocationId={pickupLocationId}
              region={record?.region}
              confirmedDelivery={
                record?.delivery_confirmed_at
                  ? {
                      method: record.delivery_method,
                      day: record.delivery_day,
                      window: record.delivery_window,
                    }
                  : null
              }
              deliveryNote={deliveryNote}
              officePickupRule={officePickupRule}
              savedDay={record?.delivery_day}
              overrideReason={overrideReason}
              onOverrideReasonChange={setOverrideReason}
              primaryClientId={record?.primary_client_id}
              authorizedClientId={record?.authorized_client_id}
              payerClientId={record?.paying_client_id}
              onAddressChange={(address) => {
                setDeliveryAddress(address);
                if (address) {
                  setRecipientName(address.recipient_name || '');
                  setRecipientPhone(address.recipient_phone || '');
                  setRecipientType((address.selection as RecipientType) || '');
                }
              }}
              onDateChange={(date) => setDeliveryDate(date)}
              onWindowChange={(window) => setDeliveryWindow(window)}
              onMethodChange={(method) => setDeliveryMethod(method)}
              onPickupLocationChange={(partnerId) => setPickupLocationId(partnerId)}
              onNoteChange={(note) => setDeliveryNote(note)}
              onRecipientTypeChange={(type) => setRecipientType(type)}
              onRecipientNameChange={(name) => setRecipientName(name)}
              onRecipientPhoneChange={(phone) => setRecipientPhone(phone)}
              fetchClientData={async (clientId) => {
                try {
                  const response = await dataProvider.getOne('clients', { id: clientId });
                  return response.data;
                } catch (error) {
                  console.error('Error fetching client:', error);
                  return null;
                }
              }}
            />
          </Box>
        )}

        {/* Review Request Section for DELIVERED status */}
        {selectedStatus === CremationStatus.DELIVERED && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={askForReview}
                  onChange={(e) => setAskForReview(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  Ask the customer for a review
                </Typography>
              }
            />
            
            {askForReview && (
              <Box sx={{ mt: 2, pl: 4 }}>
                {/* Recipient Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="review-recipient-label">Send to</InputLabel>
                  <Select
                    labelId="review-recipient-label"
                    value={reviewRecipientClientId}
                    label="Send to"
                    onChange={(e) => setReviewRecipientClientId(e.target.value as number)}
                  >
                    {clientOptions.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name} ({client.type} Client)
                      </MenuItem>
                    ))}
                    {clientOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        <em>No clients available</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {/* Send Method Selection */}
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    Send via
                  </FormLabel>
                  <RadioGroup
                    row
                    value={reviewSendMethod}
                    onChange={(e) => setReviewSendMethod(e.target.value as ReviewRequestSendMethod)}
                  >
                    <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="both" control={<Radio />} label="Both" />
                  </RadioGroup>
                </FormControl>

                {/* Message Input */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Message"
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  helperText={`This message will be sent to the customer in ${REVIEW_REQUEST_DELAY_DAYS} days`}
                  sx={{ mb: 2 }}
                />

                {/* Scheduled Date Info */}
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: '#e3f2fd', 
                  borderRadius: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1
                }}>
                  <ScheduleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Will be sent on: <strong>{moment().add(REVIEW_REQUEST_DELAY_DAYS, 'days').format('MMMM D, YYYY')}</strong>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Aftercare email opt-out for DELIVERED status */}
        {selectedStatus === CremationStatus.DELIVERED && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendAftercareEmail}
                  onChange={(e) => setSendAftercareEmail(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  Send aftercare email
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
              {sendAftercareEmail
                ? `Paul's check-in email goes out automatically ${AFTERCARE_DELAY_DAYS} days after delivery (${moment().add(AFTERCARE_DELAY_DAYS, 'days').format('MMMM D, YYYY')}).`
                : 'No aftercare email will be sent for this case.'}
            </Typography>
          </Box>
        )}

        {/* Future Feature: Show notification preview that will be sent to customer */}
        {selectedStatus && (
          <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview of customer notification (future feature):
              </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
              Your cremation process has been updated to "{statusConfig[selectedStatus]?.label}"
            </Typography>
          </Box>
            )}

        {pocWarning !== null && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {pocWarning.length > 0
              ? `Missing fields for POC, please create it manually: ${pocWarning.join(', ')}.`
              : 'Could not check the POC fields, please create it manually if it is missing.'}
          </Alert>
        )}
          </>
        );
  };
      
  // Update Dialog Actions
  const renderDialogActions = () => {
    if (selectedStatus === CremationStatus.TRANSPORT_DISPATCHED) {
        return (
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
      );
    }

    // Status already saved; only the POC warning is left to read.
    if (pocWarning !== null) {
      return <Button onClick={() => setDialogOpen(false)}>Close</Button>;
    }

        return (
          <>
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
          onClick={() => handleStatusChange(selectedStatus as CremationStatus)} 
          disabled={
            !selectedStatus ||
            loading ||
            // A pickup saved without a location is what makes the family's
            // message name the wrong city, so the save is held rather than
            // writing null and finding out on the morning of the collection.
            (selectedStatus === CremationStatus.DELIVERY_SCHEDULED &&
              (deliveryMethod === 'office_pickup' ||
                deliveryMethod === 'crematorium_pickup') &&
              !pickupLocationId) ||
            (selectedStatus === CremationStatus.DELIVERY_SCHEDULED &&
              dayTooEarly &&
              !overrideReason.trim())
          }
                color="primary" 
          variant="contained"
              >
          {loading ? <CircularProgress size={24} /> : 'Update Status'}
              </Button>
          </>
        );
  };

  return (
    <>
      {!dialogOnly && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ p: 2, mb: 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ mr: 2 }}>Current Status:</Typography>
                <Chip
                  icon={React.cloneElement(
                    statusConfig[currentStatus as CremationStatus]?.icon || <NoteAltIcon />,
                    { style: { color: 'rgba(0, 0, 0, 0.87)' } }
                  )}
                  label={statusConfig[currentStatus as CremationStatus]?.label || 'Not Started'}
                  sx={{
                    bgcolor: statusConfig[currentStatus as CremationStatus]?.color || '#757575',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontWeight: 500,
                    px: 1
                  }}
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
                disabled={currentStatus === CremationStatus.DELIVERED || loading}
              >
                Update Status
              </Button>
            </Box>

            {/* Ontario two-step chain only: a distinct drop-off means there is
                an internal handoff to record. Quebec cases drop off at the
                crematorium itself and never render this. */}
            {!!record?.dropoff_partner_id &&
              String(record.dropoff_partner_id) !== String(record.crematorium_partner_id) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: record.dropoff_released_at ? 'action.hover' : 'warning.50',
                    border: '1px solid',
                    borderColor: record.dropoff_released_at ? 'divider' : 'warning.light',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {record.dropoff_released_at
                      ? `Released to crematorium ${moment(record.dropoff_released_at).format('MMM D, h:mm A')}${
                          record.dropoff_released_by ? ` by ${record.dropoff_released_by}` : ''
                        }`
                      : 'At the drop-off location — not yet released to the crematorium'}
                  </Typography>
                  <Button
                    size="small"
                    variant={record.dropoff_released_at ? 'text' : 'contained'}
                    color={record.dropoff_released_at ? 'inherit' : 'primary'}
                    onClick={handleToggleDropoffRelease}
                    disabled={loading}
                  >
                    {record.dropoff_released_at ? 'Undo release' : 'Release to crematorium'}
                  </Button>
                </Box>
              )}

            <Box sx={{ maxHeight: '300px', overflow: 'auto', mb: 2 }}>
              {renderTimelineEvents()}
            </Box>
          </Box>
        </Box>
      )}

      {/* Status Update Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Update Cremation Status</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions>
          {renderDialogActions()}
        </DialogActions>
      </Dialog>

      {/* Ready for Delivery is the one status that messages the family by
          itself — the status write fires the invite whoever made it. Staff
          believed manual changes were silent, so the question is asked here,
          before the text goes, rather than discovered afterwards. */}
      <Dialog open={showNotifyPrompt} onClose={() => setShowNotifyPrompt(false)}>
        <DialogTitle>Ready for Delivery</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This normally sends the family a text and an email with their
            scheduling link, so they can choose when to receive the ashes.
          </Typography>
          <Typography
            component="label"
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={notifyFamilyOnReady}
              onChange={(e) => setNotifyFamilyOnReady(e.target.checked)}
            />
            Send the family their scheduling link
          </Typography>
          {!notifyFamilyOnReady && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Nothing will be sent. Setting this status again with the box
              ticked will send it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNotifyPrompt(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowNotifyPrompt(false);
              // Both questions are answered by the time this runs.
              void handleStatusChange(
                CremationStatus.READY_FOR_DELIVERY,
                true,
                true,
              );
            }}
          >
            {notifyFamilyOnReady ? 'Set and notify' : 'Set without notifying'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backward Navigation Confirmation Dialog */}
      <Dialog open={showBackwardConfirmation} onClose={handleCancelBackwardNavigation}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to move the cremation status backward from{' '}
            <strong>{statusConfig[currentStatus as CremationStatus]?.label}</strong> to{' '}
            <strong>{pendingBackwardStatus ? statusConfig[pendingBackwardStatus].label : ''}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action may affect the cremation workflow. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBackwardNavigation}>Cancel</Button>
          <Button
            onClick={handleConfirmBackwardNavigation}
            color="warning"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CremationTracker; 